import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Plus, Pencil, Trash2, X, Upload, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ImageUploadField from '../../components/admin/ImageUploadField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const empty = {
  name: '', description: '', category: 'archivio', image_url: '', featured: false, price: 0, available: true,
  name_golosi: '', ean: '', codart: '', name_mdd: '', prezzo_mdd: 0, prezzo_golosi: 0, acquisto: 0, ricarico_percent: 0,
};

const CANONICAL_HEADERS = ['Name_golosi', 'Ean', 'CodArt', 'Name_mdd', 'Prezzo_mdd', 'Prezzo_golosi', 'Acquisto', '%Ricarico'];

function cleanHeader(value) {
  return String(value ?? '').replace(/^\uFEFF/, '').trim();
}

function splitLine(line, sep) {
  const out = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i++; }
      else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function parseImportText(text) {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const first = lines[0];
  const sep = first.includes('\t') ? '\t' : first.includes(';') ? ';' : ',';
  const headers = splitLine(first, sep).map(cleanHeader);
  const rows = lines.slice(1).map(line => {
    const vals = splitLine(line, sep);
    const row = {};
    headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    return row;
  }).filter(row => Object.values(row).some(v => String(v).trim()));
  return { headers, rows };
}

function parseImportWorkbook(buffer) {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { headers: [], rows: [] };
  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  const headerRowIndex = matrix.findIndex(row => row.some(cell => cleanHeader(cell)));
  if (headerRowIndex < 0) return { headers: [], rows: [] };
  const headers = matrix[headerRowIndex].map(cleanHeader);
  const rows = matrix.slice(headerRowIndex + 1).map(line => {
    const row = {};
    headers.forEach((h, i) => { if (h) row[h] = line[i] ?? ''; });
    return row;
  }).filter(row => Object.values(row).some(v => String(v).trim()));
  return { headers: headers.filter(Boolean), rows };
}

export default function AdminProdotti() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importMode, setImportMode] = useState('upsert');
  const [importing, setImporting] = useState(false);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty,
      ...p,
      name: p.name,
      description: p.description,
      category: p.category,
      image_url: p.image_url,
      featured: p.featured,
      price: p.price ?? 0,
      available: p.available !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.description || !form.image_url) { toast.error('Compila tutti i campi obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API}/products/${editing.id}`, form); toast.success('Prodotto aggiornato!'); }
      else { await axios.post(`${API}/products`, form); toast.success('Prodotto aggiunto!'); }
      setModalOpen(false); fetch();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/products/${id}`); toast.success('Prodotto eliminato'); setDeleteId(null); fetch(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleImportText = (text) => {
    const parsed = parseImportText(text);
    setImportHeaders(parsed.headers);
    setImportRows(parsed.rows);
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (['xlsx', 'xls'].includes(ext)) {
      const parsed = parseImportWorkbook(await file.arrayBuffer());
      setImportHeaders(parsed.headers);
      setImportRows(parsed.rows);
      return;
    }
    handleImportText(await file.text());
  };

  const runImport = async () => {
    if (!importRows.length) {
      toast.error('Nessuna riga da importare');
      return;
    }
    if (missingHeaders.length) {
      toast.error(`Mancano colonne obbligatorie: ${missingHeaders.join(', ')}`);
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/products/import`, { rows: importRows, mode: importMode });
      if (!res.data.imported) {
        toast.error('Nessun prodotto importato: controlla la colonna Name_golosi');
        return;
      }
      toast.success(`Import completato: ${res.data.imported} righe, totale ${res.data.total}`);
      setImportOpen(false);
      setImportRows([]);
      setImportHeaders([]);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante import prodotti');
    } finally {
      setImporting(false);
    }
  };

  const missingHeaders = CANONICAL_HEADERS.filter(h => !importHeaders.includes(h));
  const catLabel = { archivio: 'Archivio', freschi: 'Freschi', specialita: 'Specialit\u00e0', conserve: 'Conserve' };

  return (
    <div className="min-w-0 w-full">
      <div className="flex items-center justify-between mb-4 sm:mb-8">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">Prodotti</h2>
          <p className="font-body text-gray-500 text-xs sm:text-sm mt-0.5">{products.length} nel catalogo</p>
        </div>
        {/* Mobile: icona + testo corto — Desktop: testo completo */}
        <div className="flex gap-2">
          <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-xl gap-2 flex-shrink-0">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Importa archivio</span>
            <span className="sm:hidden">Import</span>
          </Button>
          <Button onClick={openAdd} className="bg-primary hover:bg-primary-dark text-white rounded-xl gap-2 flex-shrink-0">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nuovo Prodotto</span>
            <span className="sm:hidden">Nuovo</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl h-16 animate-pulse border border-gray-100" />)}</div>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="sm:hidden space-y-1.5">
            {products.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-2.5 w-full overflow-hidden">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} loading="lazy"
                    className="w-11 h-11 object-cover rounded-lg flex-shrink-0"
                    onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2244%22 height=%2244%22><rect fill=%22%23f3f4f6%22 width=%2244%22 height=%2244%22/></svg>'; }} />
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-gray-100 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-body font-semibold text-gray-900 text-sm truncate">{p.name}</p>
                  <div className="flex flex-wrap items-center gap-1 mt-0.5">
                    <span className="text-xs text-gray-400">{catLabel[p.category] || p.category}</span>
                    {p.price > 0 && <span className="text-xs font-semibold text-primary">€{Number(p.price).toFixed(2)}</span>}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${p.available !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {p.available !== false ? 'Disp.' : 'Esaurito'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-0.5 flex-shrink-0">
                  <button onClick={() => openEdit(p)} className="p-2 hover:bg-blue-50 rounded-lg text-blue-500 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteId(p.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {products.length === 0 && <div className="text-center py-12 text-gray-400 font-body text-sm">Nessun prodotto trovato</div>}
          </div>

          {/* Desktop table view */}
          <div className="hidden sm:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Immagine', 'Nome', 'CodArt/EAN', 'Categoria', 'Prezzo', 'Acquisto', 'Disp.', 'Azioni'].map(h => (
                    <th key={h} className={`px-6 py-4 font-body text-xs text-gray-500 uppercase tracking-wider ${h === 'Azioni' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} loading="lazy" decoding="async" className="w-16 h-12 object-cover rounded-lg" onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2264%22 height=%2248%22><rect fill=%22%23f3f4f6%22 width=%2264%22 height=%2248%22/></svg>'; }} />
                      ) : (
                        <div className="w-16 h-12 rounded-lg bg-gray-100" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-body font-semibold text-gray-900">{p.name}</p>
                      <p className="font-body text-xs text-gray-400 line-clamp-1 mt-0.5">{p.description}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-body text-xs font-semibold text-gray-700">{p.codart || '—'}</p>
                      <p className="font-body text-xs text-gray-400">{p.ean || ''}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full font-body text-xs font-medium">{catLabel[p.category] || p.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      {p.price > 0
                        ? <span className="font-body font-semibold text-primary">€ {Number(p.price).toFixed(2)}</span>
                        : <span className="font-body text-xs text-gray-400 italic">Su richiesta</span>}
                    </td>
                    <td className="px-6 py-4">
                      {p.acquisto > 0
                        ? <span className="font-body text-xs text-gray-600">€ {Number(p.acquisto).toFixed(2)}</span>
                        : <span className="font-body text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full font-body text-xs font-medium ${p.available !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {p.available !== false ? 'Sì' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(p)} className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteId(p.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && <div className="text-center py-16 text-gray-400 font-body">Nessun prodotto trovato</div>}
          </div>
        </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl rounded-t-2xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0">
              <h3 className="font-heading text-lg sm:text-xl font-bold">{editing ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</h3>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Nome *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome del prodotto" className="rounded-xl" />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Descrizione *</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-xl resize-none" />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Categoria</Label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                  <option value="archivio">Archivio base</option>
                  <option value="freschi">Prodotti Freschi</option>
                  <option value="specialita">Specialit\u00e0 Locali</option>
                  <option value="conserve">Conserve</option>
                </select>
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Immagine *</Label>
                <ImageUploadField
                  value={form.image_url}
                  onChange={v => setForm({ ...form, image_url: v })}
                  previewClass="h-24"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">CodArt</Label>
                  <Input value={form.codart || ''} onChange={e => setForm({ ...form, codart: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">EAN</Label>
                  <Input value={form.ean || ''} onChange={e => setForm({ ...form, ean: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Nome MDD</Label>
                  <Input value={form.name_mdd || ''} onChange={e => setForm({ ...form, name_mdd: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Acquisto (€)</Label>
                  <Input type="number" min="0" step="0.01" value={form.acquisto || 0} onChange={e => setForm({ ...form, acquisto: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Prezzo (€)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    placeholder="0.00 — lascia vuoto"
                    className="rounded-xl"
                  />
                  <p className="font-body text-xs text-gray-400 mt-1">Lascia 0 per «prezzo su richiesta»</p>
                </div>
                <div className="flex flex-col justify-center gap-3">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="available" checked={form.available} onChange={e => setForm({ ...form, available: e.target.checked })} className="w-4 h-4 accent-primary" />
                    <Label htmlFor="available" className="font-body text-sm text-gray-700 cursor-pointer">Disponibile</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="featured" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-primary" />
                    <Label htmlFor="featured" className="font-body text-sm text-gray-700 cursor-pointer">In evidenza</Label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 sm:rounded-b-2xl flex-shrink-0">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary-dark text-white rounded-xl">
                {saving ? 'Salvataggio...' : editing ? 'Aggiorna' : 'Aggiungi'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">Elimina Prodotto</h3>
            <p className="font-body text-gray-500 mb-6">Sei sicuro? Questa azione \u00e8 irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-xl font-bold">Import archivio prodotti</h3>
              </div>
              <button onClick={() => setImportOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm font-body text-amber-800">
                Carica Excel, CSV o TSV con intestazioni:
                <code className="block mt-1 text-xs">Name_golosi, Ean, CodArt, Name_mdd, Prezzo_mdd, Prezzo_golosi, Acquisto, %Ricarico</code>
              </div>
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.txt,.tsv"
                onChange={e => handleImportFile(e.target.files?.[0])}
                className="block w-full text-sm font-body"
              />
              <textarea
                rows={6}
                placeholder="Oppure incolla qui le righe copiate da Excel..."
                onChange={e => handleImportText(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-body">Righe rilevate</p>
                  <p className="font-heading text-2xl font-bold">{importRows.length}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 md:col-span-2">
                  <p className="text-xs text-gray-500 font-body mb-1">Intestazioni</p>
                  <p className="font-body text-xs text-gray-700 break-all">{importHeaders.join(' · ') || '—'}</p>
                  {missingHeaders.length > 0 && importHeaders.length > 0 && (
                    <p className="font-body text-xs text-red-600 mt-1">Mancano: {missingHeaders.join(', ')}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 font-body text-sm">
                  <input type="radio" checked={importMode === 'upsert'} onChange={() => setImportMode('upsert')} />
                  Aggiorna/aggiungi
                </label>
                <label className="flex items-center gap-2 font-body text-sm">
                  <input type="radio" checked={importMode === 'replace'} onChange={() => setImportMode('replace')} />
                  Sostituisci archivio
                </label>
              </div>
              {importRows[0] && (
                <div className="overflow-x-auto border rounded-xl">
                  <table className="w-full text-xs font-body">
                    <thead className="bg-gray-50">
                      <tr>{CANONICAL_HEADERS.map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {importRows.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-t">
                          {CANONICAL_HEADERS.map(h => <td key={h} className="px-2 py-2">{r[h]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
              <Button variant="outline" onClick={() => setImportOpen(false)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={runImport} disabled={importing || !importRows.length || missingHeaders.length > 0} className="flex-1 bg-primary hover:bg-primary-dark text-white rounded-xl">
                {importing ? 'Import in corso...' : 'Importa prodotti'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
